import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Card, Field, Heading, Input, Text } from "@nbs/ui";
import { BrandMark } from "@/components/BrandMark";
import { useAuth } from "@/hooks/useAuth";
import { useAuthConnectivity } from "@/hooks/useAuthConnectivity";
import { useNotification } from "@/hooks/useNotification";
import { LoadingScreen } from "@/components/LoadingScreen";
import { appConfig } from "@/services/config";
import type { DemoLoginError } from "@/services/auth";

export function LoginPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const connectivity = useAuthConnectivity();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const isProduction = appConfig.environment === "production";
  const from =
    typeof location.state === "object" &&
    location.state !== null &&
    "from" in location.state &&
    typeof (location.state as { from?: unknown }).from === "string"
      ? (location.state as { from: string }).from
      : "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [error, setError] = useState<DemoLoginError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (auth.isLoading) {
    return <LoadingScreen />;
  }

  if (auth.isAuthenticated) {
    return <Navigate to={from === "/login" ? "/" : from} replace />;
  }

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await auth.loginWithPassword(
      email,
      password,
      isProduction ? "online" : connectivity.mode,
      {
        keepSignedIn,
        allowDemoFallback: !isProduction,
      },
    );
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      notify(t(`pages.loginDemo.errors.${result.error}`), "error");
      return;
    }
    notify(t("pages.loginDemo.success", { name: result.session.user.displayName }), "success");
    void navigate(from === "/login" ? "/" : from, { replace: true });
  }

  const modeLabel =
    connectivity.mode === "online"
      ? t("pages.loginDemo.modeOnline")
      : t("pages.loginDemo.modeOffline");

  return (
    <section className="nbs-login-page">
      <Card className="nbs-login-page__card">
        <BrandMark size={28} className="nbs-login-page__mark" />
        <Heading level={1}>{t("pages.loginTitle")}</Heading>
        <Text tone="muted" className="nbs-login-page__tagline">
          {t("pages.loginTagline")}
        </Text>

        <p
          className={`nbs-login-page__mode nbs-login-page__mode--${connectivity.mode}`}
          role="status"
          aria-live="polite"
        >
          {connectivity.isProbing ? t("pages.loginDemo.modeProbing") : modeLabel}
        </p>

        <form className="nbs-login-page__demo-form" onSubmit={handleLogin}>
          <Field label={t("pages.loginDemo.emailLabel")} htmlFor="demo-login-email">
            <Input
              id="demo-login-email"
              type="email"
              autoComplete="email"
              required
              placeholder={isProduction ? "user@example.com" : "admin@nbs.local"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label={t("pages.loginDemo.passwordLabel")} htmlFor="demo-login-password">
            <Input
              id="demo-login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          <label className="nbs-login-page__keep" htmlFor="demo-login-keep">
            <input
              id="demo-login-keep"
              type="checkbox"
              checked={keepSignedIn}
              onChange={(e) => setKeepSignedIn(e.target.checked)}
            />
            <span className="nbs-login-page__keep-copy">
              <span className="nbs-login-page__keep-title">
                {t("pages.loginDemo.keepSignedIn")}
              </span>
              <span className="nbs-login-page__keep-hint">
                {t("pages.loginDemo.keepSignedInHint")}
              </span>
            </span>
          </label>

          {error ? (
            <Text size="sm" className="nbs-login-page__demo-error" role="alert">
              {t(`pages.loginDemo.errors.${error}`)}
            </Text>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || (!isProduction && connectivity.isProbing)}
          >
            {submitting ? t("pages.loginDemo.submitting") : t("pages.loginDemo.submit")}
          </Button>
        </form>
      </Card>
    </section>
  );
}
